export interface QuestionMedia {
  readonly imageUrl: string;
  readonly sourceUrl: string;
  readonly attribution: string;
}

type FetchResponse = {
  readonly ok: boolean;
  json(): Promise<unknown>;
};

export type QuestionMediaFetch = (url: string) => Promise<FetchResponse>;

export interface QuestionMediaFinder {
  find(searchQuery: string): Promise<QuestionMedia | null>;
}

const COMMONS_API = "https://commons.wikimedia.org/w/api.php";
const SUPPORTED_MIME_TYPES = new Set(["image/gif", "image/jpeg", "image/png", "image/webp"]);

/**
 * Best-effort question decoration. Callers only provide a curated search phrase;
 * provider details, validation, attribution, caching, timeouts, and failures stay here.
 */
export function createQuestionMediaFinder(
  request: QuestionMediaFetch = (url) => fetch(url),
  timeoutMs = 2_500,
): QuestionMediaFinder {
  const cache = new Map<string, Promise<QuestionMedia | null>>();
  return {
    find(searchQuery: string): Promise<QuestionMedia | null> {
      const query = searchQuery.trim().slice(0, 120);
      if (!query) return Promise.resolve(null);
      const cached = cache.get(query);
      if (cached) return cached;
      const result = withTimeout(findOnCommons(query, request), timeoutMs).catch(() => null);
      cache.set(query, result);
      return result;
    },
  };
}

async function findOnCommons(
  searchQuery: string,
  request: QuestionMediaFetch,
): Promise<QuestionMedia | null> {
  const parameters = new URLSearchParams({
    action: "query",
    format: "json",
    formatversion: "2",
    origin: "*",
    generator: "search",
    gsrnamespace: "6",
    gsrlimit: "6",
    gsrsearch: `${searchQuery} filetype:bitmap`,
    prop: "imageinfo",
    iiprop: "url|mime|extmetadata",
    iiurlwidth: "900",
    iiextmetadatafilter: "Artist|LicenseShortName",
  });
  const response = await request(`${COMMONS_API}?${parameters.toString()}`);
  if (!response.ok) return null;
  return parseCommonsResponse(await response.json());
}

function parseCommonsResponse(value: unknown): QuestionMedia | null {
  if (!isRecord(value) || !isRecord(value.query) || !isUnknownArray(value.query.pages)) return null;
  for (const page of value.query.pages) {
    if (!isRecord(page) || !isUnknownArray(page.imageinfo)) continue;
    const info = page.imageinfo[0];
    if (!isRecord(info) || typeof info.mime !== "string" || !SUPPORTED_MIME_TYPES.has(info.mime)) {
      continue;
    }
    const imageUrl = validWikimediaUrl(info.thumburl) ?? validWikimediaUrl(info.url);
    const sourceUrl = validWikimediaUrl(info.descriptionurl);
    if (!imageUrl || !sourceUrl) continue;
    const metadata = isRecord(info.extmetadata) ? info.extmetadata : {};
    const artist = metadataValue(metadata.Artist);
    const license = metadataValue(metadata.LicenseShortName);
    const attribution = [artist, license, "Wikimedia Commons"].filter(Boolean).join(" · ");
    return { imageUrl, sourceUrl, attribution };
  }
  return null;
}

function metadataValue(value: unknown): string {
  if (!isRecord(value) || typeof value.value !== "string") return "";
  return value.value
    .replace(/<[^>]*>/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
}

function validWikimediaUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || !url.hostname.endsWith(".wikimedia.org")) return null;
    return url.toString();
  } catch {
    return null;
  }
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | null> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(null);
    }, timeoutMs);
    void promise.then(
      (value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      },
      () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(null);
      },
    );
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}
